import { createAsyncThunk } from "@reduxjs/toolkit";
import { apiRequest, errorFormatHandler } from "../../helpers/apiHelper";
import {
  RecallAwbInterface,
  RecallAwbPrintDataInterface,
  RecallAwbPrintDirection,
  RecallClientReceiptsDataInterface,
  RecallDocumentInterface,
  RecallEmailInterface,
  RecallEntryDetailsDataInterface,
  RecallEntrySummaryInterface,
  RecallLineInputInterface,
  RecallReceiptInputInterface,
  RecallReturnDataInterface,
  RecallScanDataInterface,
} from "../interfaces/recall.interface";

export const getRecallEntries = createAsyncThunk(
  "/recall/getRecallEntries",
  async (
    params: {
      page: number;
      perPage: number;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { page, perPage } = params;
      const { data } = await apiRequest("/recall/entries", "GET", {
        page,
        per_page: perPage,
      });
      return data;
    } catch (err: any) {
      console.warn("/recall/getRecallEntries", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postCreateRecallEntry = createAsyncThunk(
  "/recall/postCreateRecallEntry",
  async (
    params: {
      documentNumber: string;
      lines: RecallLineInputInterface[];
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/recall/entries", "POST", params);
      return data as RecallEntrySummaryInterface;
    } catch (err: any) {
      console.warn("/recall/postCreateRecallEntry", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getRecallEntry = createAsyncThunk(
  "/recall/getRecallEntry",
  async (params: { id: string | number }, { rejectWithValue, dispatch }) => {
    try {
      const { id } = params;
      const { data } = await apiRequest(`/recall/entries/${id}`, "GET", {
        page: 1,
        per_page: 9999,
      });
      return data as RecallEntryDetailsDataInterface;
    } catch (err: any) {
      console.warn("/recall/getRecallEntry", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getDownloadRecallDocument = createAsyncThunk(
  "/recall/getDownloadRecallDocument",
  async (
    params: {
      idCampaign: number;
      clientPlId: number;
      fileName?: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { idCampaign, clientPlId, fileName } = params;
      const { data } = await apiRequest(
        `/recall/entries/${idCampaign}/clients/${clientPlId}/document`,
        "GET",
        undefined,
        undefined,
        { responseType: "blob", fileName },
      );
      return data;
    } catch (err: any) {
      console.warn("/recall/getDownloadRecallDocument", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postGenerateRecallDocument = createAsyncThunk(
  "/recall/postGenerateRecallDocument",
  async (
    params: { idCampaign: number; clientPlId: number },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { idCampaign, clientPlId } = params;
      const { data } = await apiRequest(
        `/recall/entries/${idCampaign}/clients/${clientPlId}/document`,
        "POST",
      );
      return data as RecallDocumentInterface;
    } catch (err: any) {
      console.warn("/recall/postGenerateRecallDocument", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postSendRecallEmail = createAsyncThunk(
  "/recall/postSendRecallEmail",
  async (
    params: { idCampaign: number; clientPlId: number },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { idCampaign, clientPlId } = params;
      const { data } = await apiRequest(
        `/recall/entries/${idCampaign}/clients/${clientPlId}/email`,
        "POST",
      );
      return data as RecallEmailInterface;
    } catch (err: any) {
      console.warn("/recall/postSendRecallEmail", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postRequestRecallAwb = createAsyncThunk(
  "/recall/postRequestRecallAwb",
  async (
    params: { idCampaign: number; clientPlId: number },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { idCampaign, clientPlId } = params;
      const { data } = await apiRequest(
        `/recall/entries/${idCampaign}/clients/${clientPlId}/awb`,
        "POST",
        { confirmation: "TRIMITE_AWB_IN_TMS" },
      );
      return data as RecallAwbInterface;
    } catch (err: any) {
      console.warn("/recall/postRequestRecallAwb", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postScanRecallDocument = createAsyncThunk(
  "/recall/postScanRecallDocument",
  async (params: { barcodeToken: string }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiRequest("/recall/scan", "POST", params);
      return data as RecallScanDataInterface;
    } catch (err: any) {
      console.warn("/recall/postScanRecallDocument", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postPrintRecallAwb = createAsyncThunk(
  "/recall/postPrintRecallAwb",
  async (
    params: {
      idCampaign: number;
      clientPlId: number;
      direction: RecallAwbPrintDirection;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { idCampaign, clientPlId, direction } = params;
      const { data } = await apiRequest(
        `/recall/entries/${idCampaign}/clients/${clientPlId}/awb/print`,
        "POST",
        { direction },
      );
      return data as RecallAwbPrintDataInterface;
    } catch (err: any) {
      console.warn("/recall/postPrintRecallAwb", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postReturnRecallDocument = createAsyncThunk(
  "/recall/postReturnRecallDocument",
  async (
    params: { barcodeToken: string; receipts: RecallReceiptInputInterface[] },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/recall/scan/return", "POST", {
        ...params,
        confirmation: "DOCUMENT_PRIMIT_LA_DEPOZIT",
      });
      return data as RecallReturnDataInterface;
    } catch (err: any) {
      console.warn("/recall/postReturnRecallDocument", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getRecallClientReceipts = createAsyncThunk(
  "/recall/getRecallClientReceipts",
  async (
    params: { idCampaign: number; clientPlId: number },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { idCampaign, clientPlId } = params;
      const { data } = await apiRequest(
        `/recall/entries/${idCampaign}/clients/${clientPlId}/receipts`,
        "GET",
      );
      return data as RecallClientReceiptsDataInterface;
    } catch (err: any) {
      console.warn("/recall/getRecallClientReceipts", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);
